import { router } from '@inertiajs/react'
import { ArrowLeftIcon, UserIcon, SendIcon } from 'lucide-react'
import { format } from 'date-fns'
import { useState, useEffect, useRef } from 'react'
import * as ActionCable from '@rails/actioncable'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

interface Lead {
  id: number
  name: string
  messenger_username: string | null
}

interface MessengerConnection {
  id: number
  page_name: string
  page_id: string
  business_type: string
}

interface Conversation {
  id: number
  psid: string
  lead: Lead
  messenger_connection: MessengerConnection
}

interface Message {
  id: number
  message_id: string
  content: string
  direction: 'inbound' | 'outbound'
  sent_at: string
}

interface MessengerMessageShowProps {
  conversation: Conversation
  messages: Message[]
}

export default function MessengerMessageShow({ conversation, messages: initialMessages }: MessengerMessageShowProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [messageText, setMessageText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const cableRef = useRef<ActionCable.Cable | null>(null)
  const channelRef = useRef<ActionCable.Channel | null>(null)

  const handleBack = () => {
    router.visit('/recruiting/messenger_messages')
  }

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Set up ActionCable connection
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.error('[ActionCable] No auth token found')
      return
    }

    // Create cable connection
    const cable = ActionCable.createConsumer(`/cable?token=${token}`)
    cableRef.current = cable

    // Subscribe to messenger conversation channel
    const channel = cable.subscriptions.create(
      {
        channel: 'MessengerConversationsChannel',
        conversation_id: conversation.id,
      },
      {
        connected() {
          console.log('[ActionCable] Connected to messenger conversation', conversation.id)
        },
        disconnected() {
          console.log('[ActionCable] Disconnected from messenger conversation', conversation.id)
        },
        received(data: { type: string; message: Message }) {
          console.log('[ActionCable] Received data:', data)
          if (data.type === 'new_message') {
            setMessages((prev) => [...prev, data.message])
          }
        },
      }
    )
    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
      if (cableRef.current) {
        cableRef.current.disconnect()
      }
    }
  }, [conversation.id])

  const handleSendMessage = async () => {
    if (!messageText.trim() || isSending) return

    setIsSending(true)

    try {
      // Use direct fetch instead of Inertia router to avoid modal popup
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`/recruiting/messenger_messages/${conversation.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message_text: messageText.trim() }),
      })

      if (response.ok) {
        setMessageText('')
        // Message will appear via ActionCable broadcast
      } else {
        console.error('[SendMessage] Failed:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('[SendMessage] Error:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatMessageTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm a')
    } catch {
      return ''
    }
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          {/* Header */}
          <div className="px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                data-testid="back-button"
              >
                <ArrowLeftIcon className="size-5" />
              </Button>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar className="size-10">
                  <AvatarFallback className="bg-gradient-to-br from-[#0084ff] to-[#006bd6] text-white">
                    {getInitials(conversation.lead.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl font-bold truncate">{conversation.lead.name}</h1>
                  {conversation.lead.messenger_username && (
                    <p className="text-sm text-muted-foreground truncate">
                      @{conversation.lead.messenger_username}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="px-4 lg:px-6">
            <Card>
              <CardHeader className="bg-muted/50">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <UserIcon className="size-4" />
                  <span>Via {conversation.messenger_connection.page_name}</span>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {messages.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    No messages in this conversation yet
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isOutbound = message.direction === 'outbound'

                      return (
                        <div
                          key={message.id}
                          className={cn(
                            'flex',
                            isOutbound ? 'justify-end' : 'justify-start'
                          )}
                          data-testid={`message-${message.id}`}
                        >
                          <div
                            className={cn(
                              'max-w-[70%] rounded-lg px-4 py-2',
                              isOutbound
                                ? 'bg-[#0084ff] text-white'
                                : 'bg-muted text-foreground'
                            )}
                          >
                            <p className="whitespace-pre-wrap break-words">
                              {message.content}
                            </p>
                            <p
                              className={cn(
                                'mt-1 text-xs',
                                isOutbound ? 'text-white/70' : 'text-muted-foreground'
                              )}
                            >
                              {formatMessageTime(message.sent_at)}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Message Input */}
            <Card className="mt-4">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    className="min-h-[80px] resize-none"
                    disabled={isSending}
                    data-testid="message-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || isSending}
                    size="icon"
                    className="bg-[#0084ff] hover:bg-[#006bd6] text-white h-[80px] w-[80px] shrink-0 shadow-lg shadow-[#0084ff]/20"
                    data-testid="send-button"
                  >
                    <SendIcon className="size-5" />
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
